import { Box, Flex, Spinner, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Icon } from '../../../../components/Icon'
import { EventService } from '../../../../services/Event/EventService'
import { EventDetail } from '../../../../services/Event/types'
import { ArtworkCarousel } from './ArtworkCarousel'
import { AuctionCountdown } from './AuctionCountdown'
import { AuctionScreenBadges } from './AuctionScreenBadges'
import { CurrentBidCard } from './CurrentBidCard'
import { FeaturedArtwork } from './FeaturedArtwork'
import { useAuctionScreen } from './useAuctionScreen'

/** Versão branca + magenta do logo — o `logo-dark` é preto e some no fundo da tela. */
const ABRA_LOGO_URL = '/img/logo.svg'

/**
 * Início do evento em horário local. A API devolve `date_start` como datetime ISO completo
 * ("2026-09-09T03:00:00.000000Z"), então só a parte da data serve — concatenar o valor
 * inteiro com `time_start` produz uma string inválida.
 */
const eventStartsAt = (event: EventDetail | null): string | null => {
  if (!event?.date_start || !event.time_start) return null

  // `toLocaleDateString('en-CA')` devolve YYYY-MM-DD **no fuso local**. Fatiar a string
  // pegaria o dia em UTC, que só coincide com o local enquanto o servidor estiver em UTC-3.
  const dia = new Date(event.date_start).toLocaleDateString('en-CA')

  return `${dia}T${event.time_start}`
}

/**
 * Tela do leilão (ABRA-276) — pensada para ser espelhada num telão durante o evento.
 *
 * Não é uma página de painel: não tem `PageTitle`, sidebar nem header (o `DashboardPage`
 * esconde os dois para esta rota) e ocupa a viewport inteira em posição fixa, para que o
 * gutter do layout não interfira no enquadramento das obras.
 *
 * Todo o estado ao vivo — WebSocket, fallback por polling e a rotação de 10s — está em
 * `useAuctionScreen`; aqui é só a composição visual.
 */
export default function EventAuctionScreenPage() {
  const { slug } = useParams<{ slug: string }>()
  const [event, setEvent] = useState<EventDetail | null>(null)
  const [eventError, setEventError] = useState<string | null>(null)

  useEffect(() => {
    new EventService()
      .getBySlug(slug!)
      .then((data) => setEvent(data))
      .catch(() => setEventError('Não foi possível carregar o evento.'))
  }, [slug])

  const {
    artworks,
    featured,
    featuredIndex,
    peopleCount,
    auctionEndsAt,
    auctionEnded,
    auctionFinalized,
    isConnected,
    isLoading,
    loadError,
    goToIndex,
    goToNext,
    goToPrevious
  } = useAuctionScreen({ eventId: event?.id || null })

  const errorMessage = eventError || loadError

  return (
    <Flex position="fixed" inset={0} direction="column" bg="#0F0F0F" color="white" overflow="hidden" zIndex={10}>
      <Flex align="center" justify="center" pt="3.5vh" pb="3vh" flexShrink={0}>
        <Box as="img" src={ABRA_LOGO_URL} alt="ABRA — Academia Brasileira de Arte" h="clamp(24px, 3.2vh, 52px)" />
      </Flex>

      <Flex direction="column" flex="1" minH={0} px="6vw" pb="4.5vh" gap="1.5vh">
        <Flex align="center" justify="space-between" flexShrink={0}>
          <AuctionScreenBadges auctionEnded={auctionEnded} isConnected={isConnected} peopleCount={peopleCount} />

          {/*
            Aviso discreto para a falha que acontece com a tela já montada — aí o conteúdo
            antigo continua valendo e só o alerta muda. Quando não há obra alguma, quem
            mostra o erro é o bloco central, e repetir aqui seria a mesma frase duas vezes.
          */}
          {errorMessage && artworks.length > 0 && (
            <Text fontSize="clamp(11px, 0.9vw, 20px)" color="red.300">
              {errorMessage}
            </Text>
          )}
        </Flex>

        {/*
          Sem obra e com erro, mostra o erro em vez do spinner: quando o evento não carrega,
          `eventId` fica nulo, o snapshot nunca roda e `isLoading` jamais viraria false — o
          telão giraria para sempre em vez de dizer o que aconteceu.
        */}
        {errorMessage && !artworks.length ? (
          <Flex flex="1" align="center" justify="center" direction="column" gap={4} px="10vw" textAlign="center">
            <Icon name="lni lni-warning" color="magenta.500" fontSize="clamp(32px, 4vw, 84px)" />
            <Text color="white" fontSize="clamp(16px, 1.6vw, 34px)" fontWeight="600">
              {errorMessage}
            </Text>
            <Text color="whiteAlpha.600" fontSize="clamp(12px, 1.1vw, 24px)">
              A tela volta sozinha assim que a conexão for restabelecida.
            </Text>
          </Flex>
        ) : isLoading && !artworks.length ? (
          <Flex flex="1" align="center" justify="center" direction="column" gap={4}>
            <Spinner size="xl" color="magenta.500" thickness="4px" />
            <Text color="whiteAlpha.700">Carregando o leilão...</Text>
          </Flex>
        ) : (
          <>
            <Flex flex="1" minH={0} gap="1.5vw">
              <Box flex="1.8" minW={0} display="flex" flexDirection="column">
                <FeaturedArtwork artwork={featured} />
              </Box>

              {/* Maior lance, histórico e cronômetro moram todos no mesmo card, como no mockup. */}
              <Flex flex="1" minW={0} direction="column" minH={0} gap="1.5vh" bg="#1C1C1C" borderRadius="2xl" p="1.4vw">
                <Box flex="1" minH={0} display="flex" flexDirection="column">
                  <CurrentBidCard artwork={featured} auctionEnded={auctionEnded} />
                </Box>

                <AuctionCountdown
                  auctionEndsAt={auctionEndsAt}
                  auctionEnded={auctionEnded}
                  eventStartsAt={eventStartsAt(event)}
                />
              </Flex>
            </Flex>

            {artworks.length > 0 && (
              <Box flexShrink={0}>
                <ArtworkCarousel
                  artworks={artworks}
                  featuredIndex={featuredIndex}
                  onSelect={goToIndex}
                  onNext={goToNext}
                  onPrevious={goToPrevious}
                />
              </Box>
            )}

            {!artworks.length && !isLoading && (
              <Flex flex="1" align="center" justify="center">
                <Text color="whiteAlpha.700" fontSize="clamp(12px, 1.2vw, 26px)">
                  {auctionFinalized
                    ? 'Nenhuma obra foi arrematada neste leilão.'
                    : 'Nenhuma obra aprovada e liberada para leilão neste evento.'}
                </Text>
              </Flex>
            )}
          </>
        )}
      </Flex>
    </Flex>
  )
}
